/**
 * Markup for a design preview (spec 2026-09-20-elemente-mit-designs, round 2).
 *
 * A design is a recipe, so a preview is the element's own markup with the
 * recipe's stamp classes on the root and the sections the recipe does not
 * show taken out. Plain JS on purpose: the workbench (node) and the editor
 * sidebar (Vue 2 / webpack) both use it, and neither builds TypeScript.
 */

/** Same token rule as the editor's stamp (v2eToken in model/elementStamp.ts). */
export function previewToken(value) {
  return String(value).toLowerCase().replace(/[^a-z0-9-]/g, '');
}

/** The root classes a recipe stamps, without the ones a draft brought along. */
export function recipeClasses(recipe) {
  const { show, ...kinds } = recipe;
  return ['v2-component', ...Object.entries(kinds)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([kind, value]) => `v2e-x-${previewToken(kind)}-${previewToken(value)}`)];
}

/** Replaces the root's class list; stamp classes of the source are dropped. */
/**
 * Classes a design must NOT take away: the item template (which building
 * blocks an item has and in which order) and the switches for the controls.
 * A design decides how a thing LOOKS, never what it is made of
 * (spec 2026-09-20, round 8).
 */
const KEPT_X_CLASS = /^v2e-x-(on|pos|arrows|dots|autoplay|loop|centered|nav|dotsshape|dotsplace)/;

export function withRootClasses(html, classes) {
  return html.replace(/<section\b([^>]*?)\bclass="([^"]*)"/, (_m, pre, cls) => {
    const kept = cls.split(/\s+/)
      .filter((c) => c && !c.startsWith('v2e-variant-') && (!c.startsWith('v2e-x-') || KEPT_X_CLASS.test(c)));
    const merged = [...new Set([...kept, ...classes.filter(Boolean)])];
    return `<section${pre}class="${merged.join(' ')}"`;
  });
}

/**
 * Removes every `data-v2-if` section whose key the recipe does not show.
 * Counts opening and closing tags, because a section can hold nested
 * elements of the same tag (a key figure is a div with two divs inside).
 */
export function dropHiddenSections(html, show) {
  const open = /<(\w+)([^>]*?)\sdata-v2-if="([^"]+)"([^>]*)>/;
  let out = html;
  for (;;) {
    const m = open.exec(out);
    if (!m) return out.replace(/ data-v2-kept=/g, ' data-v2-if=');
    const tag = m[1];
    const key = m[3];
    const inner = new RegExp(`<${tag}\\b|</${tag}>`, 'g');
    inner.lastIndex = m.index + m[0].length;
    let depth = 1;
    let end = out.length;
    for (let t = inner.exec(out); t; t = inner.exec(out)) {
      depth += t[0][1] === '/' ? -1 : 1;
      if (depth === 0) { end = inner.lastIndex; break; }
    }
    const whole = out.slice(m.index, end);
    // Keeping a section means marking it done, so the scan moves on.
    const kept = show.includes(key) ? whole.replace(' data-v2-if=', ' data-v2-kept=') : '';
    out = out.slice(0, m.index) + kept + out.slice(end);
  }
}

/**
 * One element's markup as the given design would draw it. The editor hands
 * over the element's INNER html, so the root section is built here: the
 * component rules key off `data-wps-name` and the stamp classes.
 */
export function designPreviewMarkup(html, recipe, elementType, rootClasses = '') {
  const show = Array.isArray(recipe.show) ? recipe.show : [];
  // An element inside a container arrives as its INNER markup, so the root is
  // built here. It has to carry the element's own classes: `withRootClasses`
  // then keeps the ones a design must not touch (the item template above all).
  const rooted = /^\s*<section\b/.test(html)
    ? html
    : `<section class="section v2-element ${rootClasses}" data-wps-version="2" data-wps-name="${elementType}" data-wps-element="true">${html}</section>`;
  return withRootClasses(dropHiddenSections(rooted, show), recipeClasses(recipe));
}

/**
 * Strips the editor's own working attributes; a preview is not editable.
 * A stored picture carries its address in `data-src` and loads it only when
 * it comes into view, so a fresh preview would show empty boxes (user find
 * 2026-09-20: "on first load it looks like this, after a design switch it is
 * right"). The preview takes the address from there.
 */
export function stripEditorAttrs(html) {
  return html
    .replace(/ (contenteditable|spellcheck|dir)="[^"]*"/g, '')
    .replace(/ data-wps-edit-id="[^"]*"/g, '')
    .replace(/ (loading|decoding)="[^"]*"/g, '')
    .replace(/<div class="padding-drag-handler-(top|bottom)">[\s\S]*?<\/div>/g, '')
    .replace(/<img\b[^>]*>/g, (tag) => {
      const lazy = /\sdata-src="([^"]+)"/.exec(tag);
      if (!lazy) return tag;
      const src = /\ssrc="([^"]*)"/.exec(tag);
      if (src && src[1]) return tag;
      return src ? tag.replace(src[0], ` src="${lazy[1]}"`) : tag.replace('<img', `<img src="${lazy[1]}"`);
    });
}
